import {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react"

import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { fetchAuthSession } from "aws-amplify/auth"

import { setErrorEventUserID } from "@/lib/ts/errorEvent"
import { getFragmentData } from "@/main/gql/__generated__"
// TODO: GetMeDocument 使っちゃってるのなんとかしたい
import {
	GetMeDocument,
	GetMeQuery,
	GetMeQueryVariables,
	SalonAllFieldFragmentDoc,
} from "@/main/gql/__generated__/graphql"

import { useLoading } from "../../../../provider/loading/useLoading"

type SalonContextType = {
	currentSalonID: number
	currentUserID: string
}
const SalonContext = createContext<SalonContextType | undefined>(undefined)

const authLinkForGetMe = setContext(async (_, { headers }) => {
	const session = await fetchAuthSession()
	const token = session.tokens?.accessToken.toString() ?? ""

	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : "",
		},
	}
})

const apolloClientForGetMe = new ApolloClient({
	// 重複コード三箇所くらいあるので、カスタムフック作るべきかも。
	link: authLinkForGetMe.concat(
		createHttpLink({
			uri: process.env.REACT_APP_STORE_MANAGEMENT_API_URL,
		})
	),
	cache: new InMemoryCache(),
})

export const SalonProvider = ({
	children,
}: {
	children: (props: {
		currentSalonID: number
		currentUserID: string
	}) => ReactNode
}) => {
	const { startLoading } = useLoading()
	const [data, setData] = useState<GetMeQuery | null>(null)
	const currentSalonID = getFragmentData(
		SalonAllFieldFragmentDoc,
		data?.me.salon
	)?.id

	const [currentUserID, setCurrentUserID] = useState<string | null>(null)

	const ctx = useMemo(
		() => ({
			currentSalonID: currentSalonID ?? 0, // 0 / "" は dummy。currentSalonID/UserID が
			currentUserID: currentUserID ?? "", //   揃うまでこのコンポーネントは null を返すため問題ない。
		}),
		[currentSalonID, currentUserID]
	)

	useEffect(() => {
		const stopLoading = startLoading()
		fetchAuthSession()
			.then((session) => {
				const sub = session.tokens?.idToken?.payload.sub ?? null
				setCurrentUserID(sub)
				// 以降のエラーイベント (fe_error) をログインユーザーに紐付ける
				if (sub) setErrorEventUserID(sub)
			})
			.finally(stopLoading)
	}, [startLoading])
	useEffect(() => {
		const stopLoading = startLoading()
		apolloClientForGetMe
			.query<GetMeQuery, GetMeQueryVariables>({
				fetchPolicy: "no-cache",
				query: GetMeDocument,
			})
			.then((result) => {
				setData(result.data)
			})
			.finally(stopLoading)
	}, [startLoading])

	if (!currentSalonID || !currentUserID) {
		return null
	}

	return (
		<SalonContext.Provider value={ctx}>
			{children({ currentSalonID, currentUserID })}
		</SalonContext.Provider>
	)
}

export const useSalon = () => {
	const context = useContext(SalonContext)
	if (context === undefined) {
		throw new Error("useContext must be used within a SalonProvider")
	}
	return context
}
